/**
 * UserStrategyCard — sidebar card for a single user strategy.
 */

import { RouteLink } from '../RouteLink/RouteLink.js';
import type { UserStrategy } from '../../types.js';

interface UserStrategyCardProps {
  strategy: UserStrategy;
  isSelected: boolean;
  onClick: () => void;
  /** Whether this strategy is pinned (SHA-43 item 3) — pinned cards float to the top. */
  isPinned?: boolean;
  /** Toggle pin state. When omitted, the pin button is not rendered. */
  onTogglePin?: () => void;
  /** Open the rename flow. When omitted, the rename button is not rendered. */
  onRename?: () => void;
  /** Open the delete-confirm flow. When omitted, the delete button is not rendered. */
  onDelete?: () => void;
}

const DESCRIPTION_MAX_CHARS = 60;

function fmtDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

const actionButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 2,
  lineHeight: 1,
  fontSize: 13,
  color: '#777',
  flexShrink: 0,
};

export function UserStrategyCard({
  strategy,
  isSelected,
  onClick,
  isPinned = false,
  onTogglePin,
  onRename,
  onDelete,
}: UserStrategyCardProps) {
  const description = strategy.description
    ? strategy.description.length > DESCRIPTION_MAX_CHARS
      ? strategy.description.slice(0, DESCRIPTION_MAX_CHARS) + '…'
      : strategy.description
    : null;

  const configCount = strategy.configCount ?? 0;

  return (
    <RouteLink
      href={`/strategies/${encodeURIComponent(strategy.id)}`}
      onNavigate={onClick}
      style={{
        display: 'block',
        padding: '12px 16px',
        cursor: 'pointer',
        borderBottom: '1px solid #2a2a2a',
        backgroundColor: isSelected ? '#1e3a5f' : 'transparent',
        transition: 'background-color 0.1s',
        textDecoration: 'none',
        color: 'inherit',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#1f2a38';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
        {isPinned && (
          <span aria-label="pinned" title="Pinned" style={{ fontSize: 11, color: '#facc15', flexShrink: 0 }}>
            &#9733;
          </span>
        )}
        <span
          title={strategy.name}
          style={{
            fontWeight: 600,
            color: '#e0e0e0',
            fontSize: 14,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {strategy.name}
        </span>
        <span style={{ fontSize: 11, color: '#666', flexShrink: 0 }}>v{strategy.version}</span>
        <span style={{ flex: 1 }} />
        {onTogglePin && (
          <button
            type="button"
            aria-label={isPinned ? 'Unpin strategy' : 'Pin strategy'}
            title={isPinned ? 'Unpin' : 'Pin'}
            style={{ ...actionButtonStyle, color: isPinned ? '#facc15' : '#777' }}
            onClick={(e) => {
              // The card itself is now a real <a href> (RouteLink) — this button
              // is a DESCENDANT of that anchor. stopPropagation() alone only stops
              // the synthetic event from reaching RouteLink's own onClick; it does
              // NOT stop the browser's native anchor-follow default action (which
              // is keyed off event.defaultPrevented, not propagation state). Both
              // calls are required or a real click here would full-page-navigate
              // to the strategy URL instead of just toggling the pin (SHA-43).
              e.preventDefault();
              e.stopPropagation();
              onTogglePin();
            }}
          >
            {isPinned ? '★' : '☆'}
          </button>
        )}
        {onRename && (
          <button
            type="button"
            aria-label="Rename strategy"
            title="Rename"
            style={actionButtonStyle}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRename();
            }}
          >
            &#9998;
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            aria-label="Delete strategy"
            title="Delete"
            style={{ ...actionButtonStyle, color: '#e57373' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }}
          >
            &#128465;
          </button>
        )}
      </div>

      {description !== null && (
        <div style={{ fontSize: 13, color: '#999', marginTop: 2 }}>{description}</div>
      )}

      <div
        style={{
          fontSize: 12,
          color: '#777',
          marginTop: 4,
          display: 'flex',
          gap: 12,
          alignItems: 'center',
        }}
      >
        <span>{configCount} {configCount === 1 ? 'config' : 'configs'}</span>
        <span>Updated {fmtDate(strategy.updatedAt)}</span>
      </div>
    </RouteLink>
  );
}
